import {
  Document as DocumentX,
  Packer,
  Paragraph,
  TextRun,
  Media,
  ISectionOptions,
  Table, TableCell,
  TableRow,
  HeadingLevel,
  AlignmentType,
  WidthType,
  Footer
} from 'docx'
import { keyBy, groupBy } from 'lodash'
import { ImageObj, exportChartToBlob } from '@/utilities/highcharts-export'
import { Question, Response } from '@/store/modules/survey-store-module'
import { ChartInfo } from '@/store/modules/livechart-reference-store-module'
import { QuestionType, SliceType } from '@shared/types/database'
import { format as dateFormat, fromUnixTime } from 'date-fns'
import { processAudience, translateCriteria } from '@/utilities/audience-mappers'
import { AppStore } from '@/store'
import * as graph from '@shared/types/graphql'
import FLoopLogo from '@/assets/img/logos/feedback-logo-blue2.png'
import SliceErrorImg from '@/assets/img/SliceError.png'
import A from '@/utilities/alpha'
import { removeHiddenResponses, respondentCountText, parentQuestions, getOpenEnd } from '../components/Survey/Utilities/SurveyUtilities'
import { SliceOption, TextAreaChartType } from '@/components/Survey//Utilities/ChartUtilities'

interface ImageRatioObj {
  data: string
  ratio: number
}
interface QuestionExportData {
  question: Question
  title:string[],
  typeTitle:string,
  imgFormat: ImageRatioObj | string | undefined,
  responseSliceByText: string | undefined,
  respondentText: string | undefined,
  formatedOpenEnds: OpenEndText[],
  sliceErr: boolean
  stimuli: {label: string, link?: string, data?: ImageRatioObj }[]
}
interface SurveyData {
  testTitle: string
  workspaceIcon?: string
  reportedText: string
  createdBy: string
  createdAt: string
  floopLogo: ImageRatioObj | undefined
  testType: string
  completedOn: string
  respondents: number
  audienceCriteria: {
    name: string,
    value: string
  }[]
  findings: string[]
  questions: Array<Question & { responses: Response[] }>
  questionExportData: QuestionExportData[]
}
interface OpenEndText {
  text: string
  level: 0 | 1 | 2 | 3
}

const font = {
  name: 'Helvetica'
}

enum DocxColor {
  BLUE='375a8b',
  GREEN='15c7a3',
  BLACK='3e3e3e',
  GRAY='65607c',
  ALT_CELL_GRAY='686578'
}

const getDataUrl = (src:string): Promise<ImageRatioObj | undefined> => {
  const image = new Image()
  image.crossOrigin = 'anonymous'

  return new Promise((resolve) => {
    image.onload = () => {
      const canvas = document.createElement('canvas')
      const ctx = canvas.getContext('2d')!
      canvas.width = image.width
      canvas.height = image.height
      const ratio = (image.width / image.height)
      ctx.drawImage(image, 0, 0)
      resolve({ data: canvas.toDataURL(), ratio })
    }
    image.onerror = (err) => {
      console.error(err)
      resolve(undefined)
    }
    image.src = src
  })
}

const getCoverPage = async (doc: DocumentX, coverData: Pick<SurveyData, 'testTitle' | 'workspaceIcon' | 'reportedText' | 'floopLogo'>): Promise<ISectionOptions> => {
  const logoImage = coverData.floopLogo ? [Media.addImage(doc, coverData.floopLogo.data, 100, 100 / coverData.floopLogo.ratio)] : []

  const coverPageFormat:ISectionOptions = {
    properties: {
      titlePage: true
    },
    footers: {
      first: new Footer({
        children: [new Paragraph({
          alignment: AlignmentType.CENTER,
          heading: HeadingLevel.HEADING_1,
          spacing: {
            line: 200
          },
          children: logoImage
        })]
      })
    },
    children: [
      new Paragraph({
        alignment: AlignmentType.CENTER,
        heading: HeadingLevel.HEADING_1,
        spacing: {
          after: 250
        },
        children: [
          new TextRun({
            text: coverData.testTitle,
            font,
            color: DocxColor.BLUE,
            bold: true,
            size: 50
          })
        ]
      }),
      new Paragraph({
        alignment: AlignmentType.CENTER,
        heading: HeadingLevel.HEADING_1,
        spacing: {
          after: 4000
        },
        children: [
          new TextRun({
            text: coverData.reportedText,
            font,
            color: DocxColor.GRAY,
            size: 28
          })
        ]
      })
    ]
  }

  if (coverData.workspaceIcon) {
    const logoObj = await getDataUrl(coverData.workspaceIcon)
    if (logoObj) {
      const clientLogoImage = Media.addImage(doc, logoObj.data, 200, 200 / logoObj.ratio)
      const logoParagraph = new Paragraph({
        alignment: AlignmentType.CENTER,
        heading: HeadingLevel.HEADING_1,
        spacing: {
          line: 200
        },
        children: [clientLogoImage]
      })

      coverPageFormat.children.push(logoParagraph)
    }
  }

  return coverPageFormat
}

const buildOverviewItem = (keyVal: Record<string, string | string[]>):Paragraph[] => {
  const overviewItems:Paragraph[] = []
  Object.keys(keyVal).forEach(k => {
    const keyText = new TextRun({
      text: `${k}: `,
      font,
      color: DocxColor.BLACK,
      size: 25
    })

    const valTexts:TextRun[] = []
    if (Array.isArray(keyVal[k])) {
      (keyVal[k] as string[]).forEach(text => {
        valTexts.push(new TextRun({
          text: text.replace(/[•\t.+]/g, ''),
          font,
          color: DocxColor.GRAY,
          size: 25
        }).break())
      })
    } else {
      valTexts.push(new TextRun({
        text: keyVal[k] as string,
        font,
        color: DocxColor.GRAY,
        size: 25
      }))
    }
    overviewItems.push(new Paragraph({
      alignment: AlignmentType.LEFT,
      heading: HeadingLevel.HEADING_1,
      children: [keyText, ...valTexts]
    }))
  })
  return overviewItems
}

const getOverviewPage = (overViewData: Pick<SurveyData, 'testType' | 'completedOn' | 'respondents' |'findings' | 'audienceCriteria' | 'createdBy' | 'createdAt'>):ISectionOptions => {
  const children = [
    new Paragraph({
      alignment: AlignmentType.LEFT,
      heading: HeadingLevel.HEADING_1,
      spacing: {
        after: 200
      },
      children: [
        new TextRun({
          text: 'Test Overview',
          font,
          color: DocxColor.BLUE,
          size: 30
        })
      ]
    }),
    ...buildOverviewItem({
      'Test type': overViewData.testType,
      'Completed On': overViewData.completedOn,
      'Created By': overViewData.createdBy,
      'Created On': overViewData.createdAt,
      Respondents: overViewData.respondents.toString(),
      'Audience Criteria': overViewData.audienceCriteria.map(a => a.value)
    })
  ]

  if (overViewData.findings[0]) {
    children.push(new Paragraph({
      alignment: AlignmentType.LEFT,
      heading: HeadingLevel.HEADING_1,
      spacing: {
        before: 800,
        after: 200
      },
      children: [
        new TextRun({
          text: 'Findings',
          font,
          color: DocxColor.BLUE,
          size: 30
        })
      ]
    }))
    children.push(new Paragraph({
      alignment: AlignmentType.LEFT,
      heading: HeadingLevel.HEADING_3,
      children: [
        new TextRun({
          text: overViewData.findings[0],
          font,
          color: DocxColor.GRAY,
          size: 25
        })
      ]
    }))
  }

  return { children }
}

const getParagraphWrap = (textRun: TextRun, options: { alignment?: AlignmentType, heading?: HeadingLevel, spacingBottom?: number} = {}) => {
  return new Paragraph({
    spacing: {
      after: options.spacingBottom || 0
    },
    alignment: options.alignment || AlignmentType.LEFT,
    heading: options.heading || HeadingLevel.HEADING_1,
    children: [textRun]
  })
}

const getResponseTextWrap = (text: string, level: OpenEndText['level'] = 3):TextRun => {
  return new TextRun({
    text: text,
    font,
    color: [DocxColor.BLUE, DocxColor.BLACK, DocxColor.GRAY, DocxColor.ALT_CELL_GRAY][level],
    size: 20,
    bold: level < 2
  })
}

const getVariantPage = async (doc: DocumentX, questionSectionData: QuestionExportData):Promise<ISectionOptions> => {
  const { typeTitle, title, stimuli } = questionSectionData
  const children: Array<Table | Paragraph> = [
    getParagraphWrap(new TextRun({
      text: typeTitle,
      font,
      color: DocxColor.BLUE,
      size: 20
    }), { spacingBottom: 100 }),
    ...title.map((t, i) => {
      const txt = new TextRun({
        text: t,
        font,
        color: DocxColor.GRAY,
        size: 27
      })
      if (i !== 0) txt.break()
      return getParagraphWrap(txt, { spacingBottom: 100 })
    })
  ]

  const variantChildren:Paragraph[] = []

  for (let index = 0; index < stimuli.length; index++) {
    const { label, link, data } = stimuli[index]
    const labelTxt = new TextRun({
      text: label,
      font,
      color: DocxColor.BLUE,
      size: 20
    })
    if (link) {
      variantChildren.push(new Paragraph({ children: [labelTxt, new TextRun(link)] }))
    } else if (data) {
      const variantImage = Media.addImage(doc, data.data, 200, 200 / data.ratio)
      variantChildren.push(new Paragraph({
        alignment: AlignmentType.CENTER,
        heading: HeadingLevel.HEADING_1,
        children: [labelTxt.break(), new TextRun(' ').break(), variantImage.break()]
      }))
    }
  }

  const table = new Table({
    width: { size: 100, type: WidthType.PERCENTAGE },
    alignment: AlignmentType.CENTER,
    columnWidths: stimuli!.map(o => 4000),
    rows: [
      new TableRow({
        children: variantChildren.map((p) => {
          return new TableCell({
            children: [p]
          })
        })
      })
    ]
  })

  children.push(table)

  return { children }
}

const getQuestionPage = async (doc: DocumentX, questionSectionData: QuestionExportData):Promise<ISectionOptions> => {
  const { typeTitle, title, respondentText, imgFormat, responseSliceByText, formatedOpenEnds } = questionSectionData
  const children: Array<Paragraph | Table> = [
    getParagraphWrap(new TextRun({
      text: typeTitle,
      font,
      color: DocxColor.BLUE,
      size: 20
    }), { spacingBottom: 100 }),
    ...title.map((t, i) => {
      const txt = new TextRun({
        text: t,
        font,
        color: DocxColor.GRAY,
        size: 27
      })
      if (i !== 0) txt.break()
      return getParagraphWrap(txt, { spacingBottom: 100 })
    })
  ]

  children.push(
    getParagraphWrap(new TextRun({
      text: respondentText,
      font,
      color: DocxColor.BLACK,
      size: 20
    }), { spacingBottom: 400 })
  )

  if (imgFormat) {
    let image
    if (typeof imgFormat === 'string') {
      image = Media.addImage(doc, imgFormat, 600, 480)
    } else {
      image = Media.addImage(doc, imgFormat.data, 600, 600 / imgFormat.ratio)
    }
    children.push(new Paragraph(image))
    if (questionSectionData.sliceErr) {
      children.push(getParagraphWrap(new TextRun({
        text: 'Insufficient data for calculations.',
        font,
        color: DocxColor.BLACK,
        size: 20
      }), { alignment: AlignmentType.CENTER }))
    }
  }

  if (questionSectionData.responseSliceByText) {
    children.push(getParagraphWrap(new TextRun({
      text: responseSliceByText,
      font,
      color: DocxColor.BLACK,
      size: 20
    }), { spacingBottom: 150 }))
  }

  if (questionSectionData.formatedOpenEnds) {
    const table = new Table({
      width: { size: 100, type: WidthType.PERCENTAGE },
      alignment: AlignmentType.LEFT,
      columnWidths: [10000],
      rows: formatedOpenEnds.map(textLevel => {
        return new TableRow({
          children: [new TableCell({
            children: [new Paragraph({
              alignment: AlignmentType.LEFT,
              heading: HeadingLevel.HEADING_3,
              indent: { left: [0, 250, 270, 270][textLevel.level] },
              children: [getResponseTextWrap(textLevel.text, textLevel.level)]
            })]
          })]
        })
      })
    })
    children.push(table)
  }

  return { children }
}

const getTextLevel = (text: string, level: OpenEndText['level']) => {
  return { text, level }
}

const sortByLength = (question: Question, responses: Response[]) => {
  return responses.sort((a, b) => {
    return getOpenEnd(a, question)!.length > getOpenEnd(b, question)!.length ? -1 : 1
  })
}

const buildOpenEnds = (question: Question, responses: Response[], chartType: TextAreaChartType | undefined) => {
  const responseTextArray: OpenEndText[] = []

  if (question.hasOtherOpt) {
    responseTextArray.push(getTextLevel('OTHER', 1))
    sortByLength(question, responses.filter(r => r.otherAnswer)).forEach((r, i) => {
      responseTextArray.push(getTextLevel(r.otherAnswer!, 2))
    })
  }
  if (question.hasExplainOpt) {
    if (question.type === QuestionType.SingleSelect || question.type === QuestionType.Rate) {
      const responsesByAnswer = groupBy(responses, r => r.answer)
      const answers = question.type === QuestionType.Rate ? Object.keys(responsesByAnswer).reverse() : Object.keys(responsesByAnswer)
      answers.forEach(answer => {
        responseTextArray.push(getTextLevel(answer, 1))
        sortByLength(question, responsesByAnswer[answer].filter(r => r.explainAnswer)).forEach((r, i) => {
          responseTextArray.push(getTextLevel(r.explainAnswer!, 2))
        })
      })
    }
  } else if (question.type === QuestionType.TextArea) {
    if (chartType === TextAreaChartType.Sentiment) {
      const responsesBySentiment = groupBy(responses, r => r.sentimentLabel)
      const sentimentsOrdered = [graph.SentimentLabel.Positive, graph.SentimentLabel.Neutral, graph.SentimentLabel.Negative]
      sentimentsOrdered.forEach(sentiment => {
        if (!responsesBySentiment[sentiment]) return
        responseTextArray.push(getTextLevel(sentiment, 1))
        sortByLength(question, responsesBySentiment[sentiment].filter(r => r.answer)).forEach((r, i) => {
          responseTextArray.push(getTextLevel(r.answer, 2))
        })
      })
    } else {
      sortByLength(question, responses.filter(r => r.answer)).forEach((r, i) => {
        responseTextArray.push(getTextLevel(r.answer, 2))
      })
    }
  }

  return responseTextArray
}

const toDocBlob = async (exportData: SurveyData, store: AppStore):Promise<Blob> => {
  const doc = new DocumentX()

  const coverPage = await getCoverPage(doc, exportData)
  const overviewPage = getOverviewPage(exportData)

  doc.addSection(coverPage)
  doc.addSection(overviewPage)

  for (let index = 0; index < exportData.questionExportData.length; index++) {
    const questionSectionData = exportData.questionExportData[index]
    let questionPage:ISectionOptions
    if (questionSectionData.question.type === QuestionType.Variant) {
      questionPage = await getVariantPage(doc, questionSectionData)
    } else {
      questionPage = await getQuestionPage(doc, questionSectionData)
    }
    doc.addSection(questionPage)
  }

  return await Packer.toBlob(doc)
}

const exportDataFromStore = async (store: AppStore, testInfo: { title: string, completedAt: number, createdAt: number, reportBy: string, type: string, createdBy?: string, audience: Pick<graph.Audience, '_id' | 'payload'> | undefined}) => {
  const workspaceIcon = store.state.workspace.workspace.payload.icon
  const chartImages = await exportChartToBlob(store)
  const reportedText = `Report created on ${dateFormat(Date.now(), 'MMM dd, yyyy')} by ` + (testInfo.reportBy)
  const floopLogo = await getDataUrl(FLoopLogo)

  let audienceCriteria:{
    name: string,
    value: string
  }[] = []
  if (testInfo.audience) {
    const processed = processAudience(testInfo.audience)
    if (processed.criteria) {
      audienceCriteria = translateCriteria(processed.criteria)
    }
  }

  const exportData = Object.assign({}, {
    testTitle: testInfo.title,
    workspaceIcon,
    floopLogo,
    reportedText,
    createdBy: testInfo.createdBy || 'Feedback Loop Admin',
    createdAt: dateFormat(fromUnixTime(testInfo.createdAt), 'MMM dd, yyyy'),
    testType: testInfo.type!,
    completedOn: dateFormat(fromUnixTime(testInfo.completedAt), 'MMM dd, yyyy'),
    respondents: store.getters.survey.respondentCount,
    audienceCriteria,
    findings: store.state.surveyConfiguration.findings?.map(finding => A.stripHTML(finding)) || [],
    questions: store.getters.survey.questionsWithResponses,
    questionExportData: await buildExportQuestionData(store.getters.survey.questionsWithResponses, store, chartImages, store.getters.livechartRefs.chartRefMap)
  })

  return exportData
}

const buildExportQuestionData = async (questions: Array<Question & { responses: Response[] }>, store: AppStore, chartImages:ImageObj[], chartReference: Record<string, ChartInfo>) => {
  const chartByQuestionId = keyBy(chartImages, c => c.questionId)
  const questionTitlesById = questions.reduce((a, q) => ({ ...a, [q.id]: A.stripHTML(q.text) }), {} as Record<string, string>)

  const questionExportData:Array<QuestionExportData> = []
  for (let index = 0; index < questions.length; index++) {
    const question = questions[index]

    let title: QuestionExportData['title']
    let typeTitle: QuestionExportData['typeTitle']
    let imgFormat: QuestionExportData['imgFormat']
    let responseSliceByText: QuestionExportData['responseSliceByText']
    let respondentText: QuestionExportData['respondentText']
    let formatedOpenEnds: OpenEndText[] = []
    let sliceErr = false
    const stimuli:{label: string, link?: string, data?: ImageRatioObj}[] = []

    if (question.type === QuestionType.Description) continue
    if (question.type === QuestionType.Variant) {
      title = (question.options![0].text).split('<br><br>').map(t => A.stripHTML(t))
      typeTitle = 'VARIANT'

      for (let index = 0; index < question.options!.length; index++) {
        const option = question.options![index]
        const label = option.configuration?.title || option.label || `Variant ${index}`

        if (option.link) {
          stimuli.push({ label, link: option.link })
        } else if (option.image) {
          const encodedimg = await getDataUrl(option.image)
          stimuli.push({ label, data: encodedimg })
        }
      }
    } else {
      title = (question.configuration?.title || question.text).split('<br><br>').map(t => A.stripHTML(t))
      typeTitle = question.type === QuestionType.TextArea ? 'OPEN-END' : question.type.toUpperCase()
      if (question.hasExplainOpt) typeTitle += ' + EXPLANATION'

      const sliceBy = chartReference[question.id]?.sliceBy

      const sliceOption:SliceOption = {
        label: questionTitlesById[sliceBy] || sliceBy || SliceType.DEFAULT,
        value: sliceBy || SliceType.DEFAULT
      }

      const filteredIndexResponses = removeHiddenResponses(question.responses)
      const isSliced = (sliceOption.value !== SliceType.DEFAULT && sliceOption.value !== question.id)
      const filteredResponses = store.getters.survey.sliceResponses(filteredIndexResponses, question, sliceOption!, isSliced, true)
      respondentText = respondentCountText(question, parentQuestions(question), filteredIndexResponses, question.responses)

      if (chartByQuestionId[question.id]) {
        imgFormat = chartByQuestionId[question.id].data
      }

      if (question.type === QuestionType.TextArea || question.hasExplainOpt || question.hasOtherOpt) {
        const chartRef = chartReference[question.id]

        if (sliceOption.value !== SliceType.DEFAULT) {
          responseSliceByText = `View by: ${sliceOption.label}` + (chartRef.selectedTab && (question.type === QuestionType.TextArea || question.variantRelationId) ? ` (${chartRef.selectedTab})` : '')
        }
        if (chartRef?.selectedTab) {
          formatedOpenEnds = buildNestedOpenEnds(question, filteredResponses, sliceOption, chartRef.selectedTab, chartRef.chartType)
        } else {
          const sliceLogo = await getDataUrl(SliceErrorImg)
          imgFormat = sliceLogo
          sliceErr = true
        }
      }
    }

    questionExportData.push({
      question,
      title,
      typeTitle,
      imgFormat,
      responseSliceByText,
      respondentText,
      formatedOpenEnds,
      stimuli,
      sliceErr
    })
  }

  return questionExportData
}

const buildNestedOpenEnds = (question: Question, filteredResponses:Response[] | Record<string, Response[]> | Record<string, Record<string, Response[]>>, sliceBy: SliceOption, selectedTab: string, chartType: TextAreaChartType | undefined) => {
  let responseTextArray: OpenEndText[] = []

  if (sliceBy.value !== SliceType.DEFAULT) {
    if (question.variantRelationId?.length) {
      responseTextArray.push(getTextLevel(selectedTab, 0))

      const responsesByVariant = (filteredResponses as Record<string, Record<string, Response[]>>)[selectedTab]
      Object.keys(responsesByVariant).forEach(variantTag => {
        responseTextArray.push(getTextLevel(variantTag, 1))
        responseTextArray = responseTextArray.concat(buildOpenEnds(question, responsesByVariant[variantTag], chartType))
      })
      responseTextArray = responseTextArray.concat(buildOpenEnds(question, (filteredResponses as Record<string, Response[]>)[selectedTab] as Response[], chartType))
    } else if (question.type === QuestionType.TextArea && chartType === TextAreaChartType.Frequency) {
      responseTextArray.push(getTextLevel(selectedTab, 0))
      responseTextArray = responseTextArray.concat(buildOpenEnds(question, (filteredResponses as Record<string, Response[]>)[selectedTab] as Response[], chartType))
    } else {
      Object.keys(filteredResponses).forEach(sliceGroup => {
        responseTextArray.push(getTextLevel(sliceGroup, 0))
        responseTextArray = responseTextArray.concat(buildOpenEnds(question, (filteredResponses as Record<string, Response[]>)[sliceGroup] as Response[], chartType))
      })
    }
  } else {
    if (question.variantRelationId?.length) {
      const responsesByVariant = filteredResponses as Record<string, Response[]>
      if (selectedTab && responsesByVariant[selectedTab]) {
        responseTextArray.push(getTextLevel(selectedTab, 0))
        responseTextArray = responseTextArray.concat(buildOpenEnds(question, responsesByVariant[selectedTab], chartType))
      } else {
        Object.keys(responsesByVariant).forEach(variantTag => {
          responseTextArray.push(getTextLevel(variantTag, 0))
          responseTextArray = responseTextArray.concat(buildOpenEnds(question, responsesByVariant[variantTag], chartType))
        })
      }
    } else {
      responseTextArray = buildOpenEnds(question, filteredResponses as Response[], chartType)
    }
  }
  return responseTextArray
}

declare global {
  interface Window { URL: {
    createObjectURL: (blob: any) => any,
    revokeObjectURL: (arg: any) => any,
  } }
}

const downloadBlob = (document: Document, window: Window, blob: Blob, filename: string) => {
  const tempAnchor = document.createElement('a')
  const url = window.URL.createObjectURL(blob)

  tempAnchor.id = 'docx-a-temp-id'
  tempAnchor.setAttribute('display', 'none')
  tempAnchor.href = url
  tempAnchor.download = filename
  document.body.appendChild(tempAnchor)
  tempAnchor.click()
  window.URL.revokeObjectURL(url)
}

export {
  toDocBlob,
  downloadBlob,
  exportDataFromStore
}
